/**
 * Built-in function implementations for the rule-engine evaluator.
 *
 * Functions available in appears_when expressions:
 *   TODAY()                    — current date as ISO string (yyyy-mm-dd)
 *   YEARS_BETWEEN(date1, date2) — integer years between two ISO dates
 *   IS_EMPTY(field)            — true if field value is empty / null / undefined / ""
 *   COUNT(repeater_path)       — number of entries in a repeater array
 */

/**
 * Returns today's date as an ISO 8601 string (yyyy-mm-dd).
 * Injected as injectable for testability — tests can override this.
 */
export let getTodayISO: () => string = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

/** Override for unit testing. */
export function setTodayOverride(fn: () => string): void {
  getTodayISO = fn;
}

/**
 * YEARS_BETWEEN(date1, date2)
 * Returns the number of complete years between two ISO date strings.
 * Positive when date2 > date1.
 */
export function yearsBetween(date1: unknown, date2: unknown): number {
  const d1 = parseISODate(String(date1));
  const d2 = parseISODate(String(date2));
  if (!d1 || !d2) return 0;

  let years = d2.getFullYear() - d1.getFullYear();
  // Adjust if the anniversary hasn't passed yet in d2's year
  const hadBirthday =
    d2.getMonth() > d1.getMonth() ||
    (d2.getMonth() === d1.getMonth() && d2.getDate() >= d1.getDate());
  if (!hadBirthday) years--;
  return years;
}

/**
 * IS_EMPTY(value)
 * Returns true when value is null, undefined, empty string, or an empty array.
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * COUNT(arrayValue)
 * Returns the length of an array value (repeater entries).
 * Returns 0 for non-arrays.
 */
export function countEntries(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  return 0;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseISODate(s: string): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
